import { useEffect, useMemo, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router';
import { useSaveTemplate, useTemplate } from '../../api/queries';
import { ImageUploader } from '../../components/ImageUploader';
import { useToast } from '../../components/Toast';
import { ErrorState, Spinner } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import type { FormDefinition, Section, Template } from '../../lib/types';
import { cx, moveItem, newId } from '../../lib/utils';
import { SectionEditor } from './SectionEditor';
import { cleanDraft, copySection, countRequired, findIssues } from './template-checks';

function toDraft(t: Template): FormDefinition {
  return {
    name: t.name,
    description: t.description,
    logoImageId: t.logoImageId,
    sections: t.sections,
  };
}

export function TemplateEditorPage() {
  const { id = '' } = useParams();
  const { data: template, isPending, error } = useTemplate(id);

  if (isPending) return <Spinner />;
  if (error)
    return (
      <ErrorState
        message={errorMessage(error)}
        action={<Link to="/admin/templates">Back to templates</Link>}
      />
    );
  // Keyed so the editor state resets cleanly if the user navigates to another template.
  return <Editor key={template.id} template={template} />;
}

function scrollToId(elementId: string, focus?: boolean) {
  // Wait for React to render the element (e.g. a section that was just added or expanded).
  requestAnimationFrame(() => {
    const el = document.getElementById(elementId);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (focus && el instanceof HTMLInputElement) {
      el.focus({ preventScroll: true });
      el.select();
    }
  });
}

function Editor({ template }: { template: Template }) {
  const [draft, setDraft] = useState<FormDefinition>(() => toDraft(template));
  const [saved, setSaved] = useState(() => JSON.stringify(toDraft(template)));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Problems are only highlighted after a save attempt, so the editor does not nag while typing.
  const [showIssues, setShowIssues] = useState(false);
  const save = useSaveTemplate(template.id);
  const toast = useToast();

  const dirty = JSON.stringify(draft) !== saved;

  // Block in-app navigation and tab closing while there are unsaved changes.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('You have unsaved changes. Leave without saving?')) blocker.proceed();
    else blocker.reset();
  }, [blocker]);
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Ctrl/Cmd + S saves.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const questionStart = useMemo(() => {
    let n = 1;
    return draft.sections.map((s) => {
      const start = n;
      n += s.questions.length;
      return start;
    });
  }, [draft.sections]);

  const totalQuestions = draft.sections.reduce((n, s) => n + s.questions.length, 0);
  const requiredCount = countRequired(draft.sections);
  const issues = useMemo(() => (showIssues ? findIssues(draft) : []), [draft, showIssues]);
  // First problem per section/question id, shown inline next to the item.
  const issueById = useMemo(() => {
    const map = new Map<string, string>();
    for (const i of issues) {
      const key = i.questionId ?? i.sectionId;
      if (key && !map.has(key)) map.set(key, i.message);
    }
    return map;
  }, [issues]);
  const sectionsWithIssues = new Set(issues.map((i) => i.sectionId));

  const sectionTargets = draft.sections.map((s) => ({ id: s.id, title: s.title }));
  const allCollapsed = draft.sections.length > 0 && collapsed.size >= draft.sections.length;

  function setSections(sections: Section[]) {
    setDraft((d) => ({ ...d, sections }));
  }

  function toggleQuestion(qid: string, open?: boolean) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (open ?? !next.has(qid)) next.add(qid);
      else next.delete(qid);
      return next;
    });
  }

  function toggleSection(sid: string, open?: boolean) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (open ?? next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  }

  function jumpToSection(sid: string) {
    toggleSection(sid, true);
    scrollToId(`section-${sid}`);
  }

  function insertSection(at: number, section: Section) {
    const list = [...draft.sections];
    list.splice(at, 0, section);
    setSections(list);
    scrollToId(`section-title-${section.id}`, true);
  }

  function addSection(at = draft.sections.length) {
    insertSection(at, { id: newId('s'), title: `Section ${at + 1}`, questions: [] });
  }

  function deleteSection(i: number) {
    const s = draft.sections[i]!;
    if (
      s.questions.length &&
      !window.confirm(`Delete “${s.title}” and its ${s.questions.length} questions?`)
    )
      return;
    setSections(draft.sections.filter((_, j) => j !== i));
  }

  function moveQuestionToSection(from: number, questionIndex: number, targetId: string) {
    const question = draft.sections[from]!.questions[questionIndex]!;
    setSections(
      draft.sections.map((s, j) => {
        if (j === from)
          return { ...s, questions: s.questions.filter((_, k) => k !== questionIndex) };
        if (s.id === targetId) return { ...s, questions: [...s.questions, question] };
        return s;
      }),
    );
    toggleSection(targetId, true);
    scrollToId(`qe-${question.id}`);
  }

  function handleSave() {
    if (!dirty || save.isPending) return;
    const cleaned = cleanDraft(draft);
    const problems = findIssues(cleaned);
    if (problems.length) {
      setShowIssues(true);
      const first = problems[0]!;
      if (first.sectionId) toggleSection(first.sectionId, true);
      if (first.questionId) toggleQuestion(first.questionId, true);
      scrollToId(
        first.questionId
          ? `qe-${first.questionId}`
          : first.sectionId
            ? `section-${first.sectionId}`
            : 'form-settings',
      );
      toast.error(
        problems.length === 1
          ? `Not saved: ${first.message.toLowerCase()}`
          : `Not saved: ${problems.length} items need attention (marked in red)`,
      );
      return;
    }
    save.mutate(cleaned, {
      onSuccess: (t) => {
        const next = toDraft(t);
        setDraft(next);
        setSaved(JSON.stringify(next));
        setShowIssues(false);
        toast.success('Template saved');
      },
      onError: (err) => toast.error(errorMessage(err)),
    });
  }

  return (
    <div className="editor">
      <div className="editor-bar">
        <Link to="/admin/templates" className="btn btn-ghost btn-sm">
          ← Templates
        </Link>
        <span className="muted small hide-sm">
          {draft.sections.length} sections · {totalQuestions} questions · {requiredCount} required
        </span>
        <div className="row tight">
          {dirty && <span className="unsaved-dot">Unsaved changes</span>}
          <button
            type="button"
            className="btn btn-ghost btn-sm hide-sm"
            onClick={() =>
              setCollapsed(allCollapsed ? new Set() : new Set(draft.sections.map((s) => s.id)))
            }
          >
            {allCollapsed ? 'Expand all' : 'Collapse all'}
          </button>
          <Link to={`/admin/templates/${template.id}/preview`} className="btn btn-secondary btn-sm">
            Preview
          </Link>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSave}
            disabled={!dirty || save.isPending}
            title="Save (Ctrl+S)"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className="editor-layout">
        <nav className="editor-outline" aria-label="Sections">
          <p className="outline-title">Sections</p>
          <ol>
            {draft.sections.map((s, i) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={cx('outline-item', sectionsWithIssues.has(s.id) && 'has-issue')}
                  onClick={() => jumpToSection(s.id)}
                >
                  <span className="outline-num">{i + 1}</span>
                  <span className="clamp-1">{s.title || 'Untitled section'}</span>
                  <span className="outline-count" title="Questions">
                    {s.questions.length}
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => addSection()}>
            + Add section
          </button>
          {issues.length > 0 && (
            <p className="outline-issues">
              {issues.length} {issues.length === 1 ? 'item needs' : 'items need'} attention
            </p>
          )}
        </nav>

        <div className="editor-main">
          <section className="card editor-anchor" id="form-settings">
            <label className="field">
              <span>Form title</span>
              <input
                className="input input-title"
                value={draft.name}
                aria-invalid={showIssues && !draft.name.trim() ? true : undefined}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Introduction shown to the client</span>
              <textarea
                className="textarea"
                rows={4}
                value={draft.description ?? ''}
                placeholder="Say why you are asking, roughly how long it takes, and that only questions marked * are required."
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              />
            </label>
            <div className="field">
              <span>Logo</span>
              <ImageUploader
                label="Logo"
                value={draft.logoImageId}
                onChange={(logoImageId) => setDraft({ ...draft, logoImageId })}
              />
            </div>
          </section>

          {draft.sections.map((s, i) => (
            <SectionEditor
              key={s.id}
              section={s}
              index={i}
              total={draft.sections.length}
              firstQuestionNumber={questionStart[i] ?? 1}
              collapsed={collapsed.has(s.id)}
              onToggleCollapsed={() => toggleSection(s.id)}
              expandedIds={expanded}
              onToggleQuestion={toggleQuestion}
              issues={issueById}
              otherSections={sectionTargets.filter((t) => t.id !== s.id)}
              onChange={(section) =>
                setSections(draft.sections.map((x, j) => (j === i ? section : x)))
              }
              onMove={(d) => setSections(moveItem(draft.sections, i, i + d))}
              onAddBelow={() => addSection(i + 1)}
              onDuplicate={() => insertSection(i + 1, copySection(s))}
              onMoveQuestionTo={(qi, target) => moveQuestionToSection(i, qi, target)}
              onDelete={() => deleteSection(i)}
            />
          ))}

          {draft.sections.length === 0 && (
            <p className="muted center">This form has no sections yet. Add one to start.</p>
          )}

          <button className="btn btn-secondary add-section" onClick={() => addSection()}>
            + Add section
          </button>
        </div>
      </div>
    </div>
  );
}
